import Papa from 'papaparse';

import { handleDataFileUpload } from 'services/s3Service';

export type DataImportUploadKind = 'csv' | 'txt' | 'parquet';

export const DATA_IMPORT_SUPPORTED_LABEL = 'CSV, TXT, or Parquet';

/** Infer upload kind from the file extension (browser MIME types are unreliable). */
export function getDataImportUploadKind(file: File): DataImportUploadKind | null {
  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith('.parquet')) {
    return 'parquet';
  }
  if (lowerName.endsWith('.txt')) {
    return 'txt';
  }
  if (lowerName.endsWith('.csv')) {
    return 'csv';
  }
  return null;
}

/** Map upload kind to the file_type stored on the data source version. */
export function apiFileTypeForUpload(kind: DataImportUploadKind, browserType: string): string {
  if (kind === 'parquet') {
    return 'parquet';
  }
  if (kind === 'txt') {
    return 'text/plain';
  }
  return browserType || 'text/csv';
}

type ProgressCallback = (percentage: number) => void;

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      resolve((event.target?.result as string) || '');
    });
    reader.addEventListener('error', () => reject(reader.error));
    reader.readAsText(file);
  });
}

function parseAndCleanCsv(content: string): Promise<Record<string, string>[]> {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<string, unknown>>(content, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (result) => {
        const filteredData = result.data
          .map((row) => {
            const cleanedRow: Record<string, string> = {};
            Object.entries(row).forEach(([key, value]) => {
              const cleanedKey = key.trim();
              if (cleanedKey !== '') {
                cleanedRow[cleanedKey] = value?.toString().replace(/,\s*$/, '').trim() ?? '';
              }
            });
            return cleanedRow;
          })
          .filter((cleanedRow) =>
            Object.values(cleanedRow).some(
              (value) => value !== null && value !== '' && value !== ' ' && value !== undefined,
            ),
          );
        resolve(filteredData);
      },
      error: (error: Error) => reject(error),
    });
  });
}

/** Upload binary files (Parquet / TXT) without client-side CSV rewriting. */
export async function uploadRawDataImportFile(
  file: File,
  bucketName: string,
  progressCallback: ProgressCallback,
): Promise<string> {
  if (file.size === 0) {
    throw new Error('EMPTY_FILE');
  }

  const contentType =
    getDataImportUploadKind(file) === 'parquet' ? 'application/octet-stream' : 'text/plain';

  const uploadFile = new File([file], file.name, { type: contentType });
  return handleDataFileUpload(uploadFile, bucketName, progressCallback);
}

/** Parse and normalise CSV in the browser, then upload as UTF-8 CSV (legacy behaviour). */
export async function uploadCsvDataImportFile(
  file: File,
  bucketName: string,
  progressCallback: ProgressCallback,
): Promise<string> {
  const content = await readFileAsText(file);
  if (!content || /^\s*$/.test(content)) {
    throw new Error('EMPTY_FILE');
  }

  const filteredData = await parseAndCleanCsv(content);
  const unParsedData = Papa.unparse(filteredData);
  if (!unParsedData) {
    throw new Error('EMPTY_FILE');
  }

  const blob = new Blob([unParsedData], {
    type: 'text/csv;charset=utf-8',
  });
  const fileData = new File([blob], file.name.endsWith('.csv') ? file.name : `${file.name}.csv`, {
    type: 'text/csv',
  });
  return handleDataFileUpload(fileData, bucketName, progressCallback);
}
